// Failed-state panel shared by the main player and multiview tiles. Every failure offers a next action (PLAY03-05).
import { ConnectionLimitDetails } from '@tuned/contracts';
import { formatTime } from '../../components/format.ts';
import type { Diagnostics } from './controller.ts';
import type { PlayerError } from './machine.ts';

const SOURCE_TEXT = {
  provider: 'This limit was reported by your provider.',
  user: 'You set this allowance in Settings.',
  default: "Your provider didn't report a limit, so Tuned allows 1; you can raise it in Settings.",
} as const;

const TITLES: Record<string, string> = {
  CONNECTION_LIMIT: 'All streams are in use',
  UNSUPPORTED_MEDIA: "This browser can't play this stream",
  SESSION_EXPIRED: 'Playback session ended',
  START_TIMEOUT: "The channel didn't start",
  CONVERSION_FAILED: "This title couldn't be converted",
};

export function ErrorPanel({ error, diagnostics, onRetry, onClose, onStopSession }: {
  error: PlayerError;
  diagnostics: Diagnostics;
  onRetry(): void;
  onClose(): void;
  onStopSession(sessionId: string): void;
}) {
  const limit = error.code === 'CONNECTION_LIMIT' ? ConnectionLimitDetails.safeParse(error.details) : null;
  const reason = typeof error.details?.reason === 'string' ? error.details.reason : null;
  return (
    <div className="tp-error" role="alert" data-testid="player-error" data-error-code={error.code}>
      <p className="tp-error-title">{TITLES[error.code] ?? "Couldn't play"}</p>
      <p>{error.message}</p>
      {reason && <p className="tp-error-reason">{reason}</p>}
      {limit?.success && (
        <div className="tp-limit">
          <p><strong>{limit.data.inUse} of {limit.data.allowance} streams in use.</strong> {SOURCE_TEXT[limit.data.allowanceSource]}</p>
          {limit.data.sessions.length > 0 && (
            <ul>
              {limit.data.sessions.map((s) => (
                <li key={s.sessionId}>
                  <span>{s.label}</span>
                  {s.thisDevice && <span className="tp-badge">This device</span>}
                  <span className="tp-muted">since {formatTime(s.startedAt)}</span>
                  {s.thisDevice && (
                    <button type="button" className="tp-btn" onClick={() => onStopSession(s.sessionId)}>Stop the stream on this device</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="tp-muted">Tuned never stops streams on other devices.</p>
        </div>
      )}
      <div className="tp-error-actions">
        <button type="button" className="tp-btn tp-btn-primary" onClick={onRetry}>Retry</button>
        <button type="button" className="tp-btn" onClick={onClose}>Close</button>
      </div>
      <details className="tp-diag">
        <summary>Diagnostics</summary>
        <dl>
          <dt>Code</dt><dd>{error.code}</dd>
          {error.requestId && <><dt>Request ID</dt><dd>{error.requestId}</dd></>}
          <dt>Engine</dt><dd>{diagnostics.engine ?? 'none'}</dd>
          <dt>Delivery</dt><dd>{diagnostics.delivery ?? 'none'}</dd>
          {diagnostics.decision && <><dt>Decision</dt><dd>{diagnostics.decision.kind}{'reason' in diagnostics.decision ? `: ${diagnostics.decision.reason}` : ''}</dd></>}
          {diagnostics.transport === 'downgraded' && <><dt>Transport</dt><dd>Unencrypted source (provider redirected HTTPS to HTTP)</dd></>}
          <dt>Last HTTP status</dt><dd>{error.httpStatus ?? diagnostics.lastHttpStatus ?? 'none'}</dd>
          {diagnostics.lastFault && <><dt>Last fault</dt><dd>{diagnostics.lastFault}</dd></>}
          {diagnostics.mediaPath?.startsWith('/media/') && <><dt>Media path</dt><dd>{diagnostics.mediaPath}</dd></>}
        </dl>
      </details>
    </div>
  );
}
